/**
 * Parses legacy AngularJS JavaScript RegExp literals without using Trim('/').
 * A slash closes the literal only when it is unescaped and outside a character class.
 */
export interface NormalizedRegexDefinition {
  pattern: string;
  flags: string;
  wasLiteral: boolean;
}

const CANONICAL_FLAG_ORDER = 'gimsuy';
const SUPPORTED_FLAGS = new Set(CANONICAL_FLAG_ORDER.split(''));
const UNSUPPORTED_FLAGS = new Set(['d', 'v']);

function normalizeFlags(rawFlags: string): string {
  const seen = new Set<string>();
  for (const flag of rawFlags.trim()) {
    if (seen.has(flag)) throw new Error(`پرچم تکراری Regex: ${flag}`);
    seen.add(flag);
    if (UNSUPPORTED_FLAGS.has(flag)) throw new Error(`پرچم Regex پشتیبانی نمی‌شود: ${flag}`);
    if (!SUPPORTED_FLAGS.has(flag)) throw new Error(`پرچم Regex معتبر نیست: ${flag}`);
  }
  return [...CANONICAL_FLAG_ORDER].filter((flag) => seen.has(flag)).join('');
}

function tryParseLiteral(raw: string): { pattern: string; flags: string } | undefined {
  if (raw.length < 2 || raw[0] !== '/') return undefined;
  let escaped = false;
  let inCharacterClass = false;
  for (let index = 1; index < raw.length; index += 1) {
    const character = raw[index] ?? '';
    if (escaped) {
      escaped = false;
      continue;
    }
    if (character === '\\') {
      escaped = true;
      continue;
    }
    if (character === '[' && !inCharacterClass) {
      inCharacterClass = true;
      continue;
    }
    if (character === ']' && inCharacterClass) {
      inCharacterClass = false;
      continue;
    }
    if (character === '/' && !inCharacterClass) {
      return { pattern: raw.slice(1, index), flags: raw.slice(index + 1) };
    }
  }
  return undefined;
}

/** Returns canonical pattern and separate flags. Invalid/unsupported flags throw. */
export function normalizeRegexDefinition(rawPattern: string | undefined, explicitFlags = ''): NormalizedRegexDefinition {
  const raw = (rawPattern ?? '').trim();
  const literal = tryParseLiteral(raw);
  const pattern = literal?.pattern ?? raw;
  if (pattern.length > 4096) throw new Error('طول عبارت Regex بیش از حد مجاز است.');
  const flags = normalizeFlags(`${literal?.flags ?? ''}${explicitFlags ?? ''}`);
  return { pattern, flags, wasLiteral: Boolean(literal) };
}

/** Maps JavaScript flags to the subset appropriate for one RegExp.test call. */
export function runtimeRegexFlags(flags: string | undefined): string {
  const canonical = normalizeFlags(flags ?? '');
  return [...canonical].filter((flag) => flag === 'i' || flag === 'm' || flag === 's').join('');
}
